/** @jsxImportSource @emotion/react */
import {
  EmptyContent,
  Header,
  useSession,
  TruncatedString,
} from '@quickbit/backoffice-core-frontend';
import {
  Typography,
  createStyles,
  Table,
  Button,
  SearchInput,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Platform } from 'tabs/types';

interface Props {
  platforms: Platform[];
}

export const PlatformList = ({ platforms }: Props) => {
  const styles = useStyles();
  const session = useSession();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>('');

  const headers = [
    { label: 'Name', cellKey: 'name' },
    { label: 'Platform ID', cellKey: 'platformId' },
    { label: '# Merchant', cellKey: 'merchantCount' },
    { label: '# Pending Merchant', cellKey: 'pendingCount' },
  ];

  const rows = useMemo(
    () =>
      platforms
        .map((platform) => ({
          value: {
            uuid: platform.uuid,
            platformId: <TruncatedString text={platform.uuid ?? ''} />,
            name: platform.name,
            deletedAt: platform.deletedAt?.toLocaleString(),
            merchantCount: platform.merchants.length,
            pendingCount: platform.pendingMerchants.length,
          },
          disabled: !!platform.deletedAt,
        }))
        .filter(({ value: { name, uuid } }) => {
          if (searchValue === '') {
            return true;
          }
          const _uuid = uuid?.toLowerCase();
          const _name = name.toLowerCase();

          return [_uuid, _name].join(' ').includes(searchValue.toLowerCase());
        }),
    [searchValue, platforms]
  );

  const hasPermission = useMemo(() => {
    if (
      session.authenticated &&
      !session.permissions.includes('edit_merchants')
    )
      return false;
    return true;
  }, [session]);

  return (
    <>
      <Header>
        <div css={[styles.headerContent]}>
          <div css={styles.titleContainer}>
            <Typography variant="subtitle">Platforms</Typography>
          </div>

          {platforms.length > 0 && (
            <SearchInput
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          )}
          <UtilityButton
            text="Add platform"
            variant="primary"
            icon="plus"
            disabled={!hasPermission}
            onClick={() => navigate('new')}
          />
        </div>
      </Header>

      {platforms.length === 0 && (
        <EmptyContent
          heading="Oops! It's empty."
          body="Get started by adding your first platform."
          action={
            <Button
              text="Add platform"
              variant="primary"
              icon="plus"
              onClick={() => navigate('new')}
            />
          }
        />
      )}

      {platforms.length > 0 && (
        <div css={styles.tableWrapper}>
          <Table
            headers={headers}
            rows={rows}
            // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
            onRowClick={(row) => navigate(`/platforms/${row.uuid}`)}
          />
        </div>
      )}
    </>
  );
};

const useStyles = createStyles(({ palette, shape, spacing }) => ({
  headerContent: {
    display: 'grid',
    gap: spacing.xxs,
    gridTemplateColumns: '1fr auto auto',
    'svg:not(button *)': {
      color: palette.grey.x900,
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  tableWrapper: {
    border: `1px solid ${palette.grey.x100}`,
    borderRadius: shape.borderRadius,
  },
}));
